/**
 * @module features/clients/pages/ClientsPage
 * @description Master Client Organization directory with fleet stats, search,
 * status filters, adaptive card/table view, slide-over drawer, and modal workflows.
 * Fulfills the 4-state contract matching UsersPage and DevicesPage.
 */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  PlusCircle,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ClientStatsCards } from "../components/ClientStatsCards";
import { ClientFilters } from "../components/ClientFilters";
import { ClientTable } from "../components/ClientTable";
import { ClientCardList } from "../components/ClientCardList";
import { ClientTableSkeleton, ClientCardSkeleton } from "../components/ClientSkeletons";
import { ClientInspectorDrawer } from "../components/ClientInspectorDrawer";
import { LinkUserModal } from "../components/LinkUserModal";
import { ClientLifecycleModal } from "../components/ClientLifecycleModal";
import { useClients, useClientStats } from "../api/clientQueries";
import { ClientSummary } from "../types";
import { AppError } from "@/lib/api-client";

export function ClientsPage() {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [status, setStatus] = React.useState("all");
  const [search, setSearch] = React.useState("");

  // Modals & Drawer State
  const [inspectingClientId, setInspectingClientId] = React.useState<string | null>(null);
  const [linkingClient, setLinkingClient] = React.useState<ClientSummary | null>(null);
  const [lifecycleClient, setLifecycleClient] = React.useState<ClientSummary | null>(null);
  const [lifecycleMode, setLifecycleMode] = React.useState<"status" | "delete" | null>(null);

  const {
    data: clientsResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useClients({
    page,
    page_size: pageSize,
    status,
    search,
  });

  const {
    data: stats,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useClientStats();

  const handleRefreshAll = React.useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  const handleSearchChange = React.useCallback((newSearch: string) => {
    setSearch(newSearch);
    setPage(1);
  }, []);

  const handleStatusChange = React.useCallback((newStatus: string) => {
    setStatus(newStatus);
    setPage(1);
  }, []);

  const clients = Array.isArray(clientsResponse?.items) ? clientsResponse.items : [];
  const pagination = clientsResponse?.pagination;
  const filtersActive = Boolean(search) || status !== "all";
  const err = error as AppError | null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with standard system primary CTA */}
      <PageHeader
        title="Client Management"
        description="Onboard client organizations, manage seat capacity, link users, and supervise multi-tenant access."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={isFetching}
              className="h-10 sm:h-9 text-xs"
              title="Refresh organizations and fleet metrics"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              size="sm"
              asChild
              className="h-10 sm:h-9 text-xs shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              <Link to="/s-admin/clients/onboard">
                <PlusCircle className="mr-1.5 h-4 w-4" />
                <span>Onboard Client</span>
              </Link>
            </Button>
          </div>
        }
      />

      {/* Fleet KPI Metric Cards */}
      <ClientStatsCards stats={stats} isLoading={isStatsLoading} />

      {/* Search & Status Filter Toolbar */}
      <ClientFilters
        search={search}
        status={status}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
      />

      {/* 4-State Contract Data Grid Rendering */}
      {isLoading ? (
        <>
          <div className="hidden sm:block">
            <ClientTableSkeleton rows={6} />
          </div>
          <div className="block sm:hidden">
            <ClientCardSkeleton count={4} />
          </div>
        </>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-xl border border-destructive/20 bg-destructive/10 p-6 sm:p-8 text-center text-destructive"
        >
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" aria-hidden="true" />
          <h2 className="mt-3 font-semibold text-base">Failed to load client organizations</h2>
          <p className="mt-1 text-xs max-w-md mx-auto text-destructive/80">
            {err?.errorDetails || err?.message || "An unexpected error occurred while communicating with the server."}
          </p>
          {err?.requestId && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground break-all">
              Request ID: {err.requestId}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="mt-4 bg-card text-foreground text-xs"
          >
            Retry Request
          </Button>
        </div>
      ) : clients.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 p-10 sm:p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Building2 className="h-6 w-6" aria-hidden="true" />
          </div>
          {filtersActive ? (
            <>
              <h2 className="mt-4 font-semibold text-base text-foreground">
                No organizations match your filters
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Try a different search term or clear active filters.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 text-xs"
                onClick={() => {
                  setSearch("");
                  setStatus("all");
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <h2 className="mt-4 font-semibold text-base text-foreground">
                No client organizations onboarded yet
              </h2>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                Get started by onboarding your first enterprise client organization.
              </p>
              <Button size="sm" asChild className="mt-4 text-xs">
                <Link to="/s-admin/clients/onboard">
                  <PlusCircle className="mr-1.5 h-4 w-4" />
                  Onboard First Client
                </Link>
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Desktop & Tablet Table (>=640px) */}
          <div className="hidden sm:block">
            <ClientTable
              clients={clients}
              onView={(client) => setInspectingClientId(client.id)}
              onEdit={(client) => navigate(`/s-admin/clients/${client.id}/edit`)}
              onLinkUsers={(client) => setLinkingClient(client)}
              onToggleStatus={(client) => {
                setLifecycleClient(client);
                setLifecycleMode("status");
              }}
              onDelete={(client) => {
                setLifecycleClient(client);
                setLifecycleMode("delete");
              }}
            />
          </div>

          {/* Mobile Card List (<640px) */}
          <ClientCardList
            clients={clients}
            onView={(client) => setInspectingClientId(client.id)}
            onEdit={(client) => navigate(`/s-admin/clients/${client.id}/edit`)}
            onLinkUsers={(client) => setLinkingClient(client)}
            onToggleStatus={(client) => {
              setLifecycleClient(client);
              setLifecycleMode("status");
            }}
            onDelete={(client) => {
              setLifecycleClient(client);
              setLifecycleMode("delete");
            }}
          />

          {/* Pagination Controls */}
          {pagination && pagination.total_pages > 1 && (
            <nav
              aria-label="Pagination"
              className="flex items-center justify-between pt-2 text-xs text-muted-foreground"
            >
              <p className="font-mono">
                {(pagination.page - 1) * pagination.page_size + 1}–
                {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of{" "}
                {pagination.total_count}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!pagination.has_prev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="h-10 sm:h-8 gap-1 text-xs"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Previous</span>
                </Button>
                <span className="font-mono px-1">
                  {pagination.page} / {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!pagination.has_next}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-10 sm:h-8 gap-1 text-xs"
                >
                  <span>Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </nav>
          )}
        </>
      )}

      {/* Slide-over Inspector Drawer */}
      <ClientInspectorDrawer
        clientId={inspectingClientId}
        open={Boolean(inspectingClientId)}
        onClose={() => setInspectingClientId(null)}
        onEdit={(client) => {
          setInspectingClientId(null);
          navigate(`/s-admin/clients/${client.id}/edit`);
        }}
        onLinkUsers={(client) => {
          setInspectingClientId(null);
          setLinkingClient(client as any);
        }}
        onToggleStatus={(client) => {
          setInspectingClientId(null);
          setLifecycleClient(client as any);
          setLifecycleMode("status");
        }}
        onDelete={(client) => {
          setInspectingClientId(null);
          setLifecycleClient(client as any);
          setLifecycleMode("delete");
        }}
      />

      {/* Link Users Modal */}
      {linkingClient && (
        <LinkUserModal
          clientId={linkingClient.id}
          clientName={linkingClient.org_name}
          maxLimit={linkingClient.max_user_limit}
          currentActiveCount={linkingClient.active_user_count}
          open={Boolean(linkingClient)}
          onClose={() => setLinkingClient(null)}
          onSuccess={() => {
            refetch();
            refetchStats();
          }}
        />
      )}

      {/* Lifecycle Modal (Status Toggle / Soft Delete) */}
      {lifecycleClient && lifecycleMode && (
        <ClientLifecycleModal
          client={lifecycleClient}
          mode={lifecycleMode}
          open={Boolean(lifecycleClient && lifecycleMode)}
          onClose={() => {
            setLifecycleClient(null);
            setLifecycleMode(null);
          }}
          onSuccess={() => {
            refetch();
            refetchStats();
          }}
        />
      )}
    </div>
  );
}
